import React from 'react'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { FormFieldGrid } from '@/components/forms/FormFieldGrid'

import { ArrayLevelError, RemoveRowButton, RowError, SourceInputs } from './SourceRow'

/**
 * An ordered list of tokens stored as a plain array of strings.
 *
 * Backs TCN's campaign-name sources and the generic connector's campaign-name paths
 * and event-value lists. What varies between them is a `codec` (how a row's controls
 * map to and from the stored string) and labelling; the row bookkeeping below is the
 * part worth having exactly once.
 */
export const TokenListFields = ({
  field,
  label,
  codec,
  sources,
  examples,
  isWholeValue,
  placeholder,
  help,
  caption,
  columnLabels = ['Path'],
  addLabel = 'Add',
  removeLabel = 'Remove',
  testIdPrefix,
  containerTestId,
  separator,
  previewLabel = (token) => token,
  children,
}) => {
  const { values, errors, setFieldValue } = useFormikContext()

  // Empty strings are kept: that is what a freshly added, not-yet-filled row looks
  // like. Non-strings are dropped rather than tolerated -- the write path validates
  // list[str], but a hand-edited row arrives here unvalidated and would throw in the
  // codec. The resolver guards the same way with isinstance checks.
  //
  // Each surviving token keeps its index in the stored array, because Yup validates
  // that array unfiltered -- so errors[field] stays indexed against it, and a dropped
  // entry would otherwise show a valid row its neighbour's message.
  const rows = (Array.isArray(values[field]) ? values[field] : [])
    .map((token, storedIndex) => ({ token, storedIndex }))
    .filter(({ token }) => typeof token === 'string')

  // Writes flatten to the filtered rows, which is what heals a corrupted column: the
  // dropped entries are gone after the first edit and the indices line up again.
  const tokens = rows.map(({ token }) => token)
  const filledTokens = tokens.filter(codec.isQualified)

  const setRow = (index, { prefix, path }) =>
    setFieldValue(
      field,
      tokens.map((token, rowIndex) => (rowIndex === index ? codec.join(prefix, path) : token))
    )
  const addRow = () => setFieldValue(field, [...tokens, ''])
  const removeRow = (index) =>
    setFieldValue(
      field,
      tokens.filter((_, rowIndex) => rowIndex !== index)
    )

  const hasSources = !isEmpty(sources)

  return (
    <FormField data-testid={containerTestId || `${testIdPrefix}-fields`}>
      <label>{label}</label>
      {help}
      {caption && <div className="form-caption">{caption}</div>}
      {!isEmpty(tokens) && (
        <FormFieldGrid>
          {hasSources && (
            <FormField span={5}>
              <label>{columnLabels[0]}</label>
            </FormField>
          )}
          <FormField span={hasSources ? 7 : 12}>
            <label>{hasSources ? columnLabels[1] : columnLabels[0]}</label>
          </FormField>
        </FormFieldGrid>
      )}
      {rows.map(({ token, storedIndex }, index) => {
        const { prefix, path } = codec.split(token)
        return (
          // eslint-disable-next-line react/no-array-index-key
          <FormFieldGrid key={index}>
            <SourceInputs
              pathLabel={hasSources ? columnLabels[1] : columnLabels[0]}
              sources={sources}
              examples={examples}
              isWholeValue={isWholeValue}
              placeholder={placeholder}
              prefix={prefix}
              path={path}
              index={index}
              testIdPrefix={testIdPrefix}
              onChange={(next) => setRow(index, next)}
            />
            <RemoveRowButton
              label={removeLabel}
              testId={`${testIdPrefix}-remove-${index}`}
              onClick={() => removeRow(index)}
            />
            <RowError message={errors[field]?.[storedIndex]} />
          </FormFieldGrid>
        )
      })}
      <Button type="button" onClick={addRow} data-testid={`${testIdPrefix}-add`}>
        {addLabel}
      </Button>
      <ArrayLevelError error={errors[field]} />
      {separator && !isEmpty(filledTokens) && (
        <div className="form-caption" data-testid={`${testIdPrefix}-preview`}>
          Preview: {filledTokens.map(previewLabel).join(separator)}
        </div>
      )}
      {children}
    </FormField>
  )
}
